import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent } from "../components/ui/dialog";
import CrudDialog from "../components/CrudDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Cable, Search, X, Cpu } from "lucide-react";

const BRANDS = ["Honda", "Yamaha", "Suzuki", "Kawasaki"];
const ECU_MAKERS = ["Keihin", "Shindengen", "Denso", "Mitsubishi", "Bosch", "Delphi", "Other"];
const CATEGORIES = ["ECU", "SCU", "Odo"];
const PINOUT_TYPES = ["ECU", "Debug", "Diagnostic"];

const BRAND_STYLE = {
  Honda: "text-red-400 border-red-700/50 bg-red-950/40",
  Yamaha: "text-blue-400 border-blue-700/50 bg-blue-950/40",
  Suzuki: "text-yellow-400 border-yellow-700/50 bg-yellow-950/40",
  Kawasaki: "text-emerald-400 border-emerald-700/50 bg-emerald-950/40",
};
const TAB_ACTIVE = {
  All: "bg-slate-200 text-slate-950 border-slate-200",
  Honda: "bg-red-500 text-white border-red-500",
  Yamaha: "bg-blue-500 text-white border-blue-500",
  Suzuki: "bg-yellow-500 text-slate-950 border-yellow-500",
  Kawasaki: "bg-emerald-500 text-slate-950 border-emerald-500",
};

const FIELDS = [
  { key: "brand", label: "Brand", type: "select", options: BRANDS },
  { key: "model", label: "Model", placeholder: "Vario 150" },
  { key: "part_number", label: "Part Number / Kode", placeholder: "30400-K59J-A71" },
  { key: "ecu_maker", label: "ECU Maker", type: "select", options: ECU_MAKERS },
  { key: "category", label: "Pinout Category", type: "select", options: CATEGORIES },
  { key: "pinout_type", label: "Pinout Type", type: "select", options: PINOUT_TYPES },
  { key: "image_url", label: "Schematic / Photo Image URL", placeholder: "https://…" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "bench_notes", label: "Bench Notes", type: "textarea" },
  { key: "pins", label: "Pins (JSON: [{pin,function,color}])", type: "textarea", placeholder: '[{"pin":"1","function":"+12V","color":"#e11d48"}]' },
];

const FALLBACK_IMG = "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

// Map common wire-color names → hex so the pin badge follows its label (e.g. "BLUE").
const WIRE_COLORS = {
  RED: "#ef4444", BLACK: "#111827", WHITE: "#f8fafc", YELLOW: "#eab308",
  GREEN: "#22c55e", BLUE: "#3b82f6", BROWN: "#92400e", ORANGE: "#f97316",
  GRAY: "#6b7280", GREY: "#6b7280", PURPLE: "#a855f7", VIOLET: "#8b5cf6",
  PINK: "#ec4899", GOLD: "#d4af37", SILVER: "#cbd5e1", CYAN: "#06b6d4",
  LIGHTBLUE: "#7dd3fc", LIGHTGREEN: "#86efac", DARKGREEN: "#15803d", DARKBLUE: "#1e3a8a",
};
const wireColor = (label) => WIRE_COLORS[String(label || "").trim().toUpperCase().replace(/[\s-_/]/g, "")];
// Pick readable text color (black/white) for a given hex background.
const textOn = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? "#111827" : "#fff";
};

export default function PinoutPage() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [brand, setBrand] = useState("Honda");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [ptype, setPtype] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const res = await api.get("/pinouts");
    setItems(res.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      if (brand !== "All" && i.brand !== brand) return false;
      if (category && (i.category || "ECU") !== category) return false;
      if (ptype && (i.pinout_type || "ECU") !== ptype) return false;
      if (needle) {
        const hay = `${i.model || ""} ${i.part_number || ""} ${i.ecu_maker || ""} ${i.description || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, brand, q, category, ptype]);

  const parsePins = (p) => {
    if (Array.isArray(p)) return p;
    try { return JSON.parse(p || "[]"); } catch { return []; }
  };

  const submit = async (form) => {
    const payload = { ...form, pins: parsePins(form.pins) };
    try {
      if (dialog.initial?.id) await api.put(`/pinouts/${dialog.initial.id}`, payload);
      else await api.post("/pinouts", payload);
      toast.success("Pinout saved");
      setDialog({ open: false, initial: null });
      load();
    } catch { toast.error("Save failed — admin only"); }
  };

  const openEdit = (item) => setDialog({ open: true, initial: { ...item, pins: JSON.stringify(item.pins || [], null, 0) } });

  const del = async (id) => {
    try { await api.delete(`/pinouts/${id}`); toast.success("Deleted"); setDetail(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const tabs = ["All", ...BRANDS];

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Cable className="h-4 w-4" /> Page 02</div>
          <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Connector &amp; Pinouts</h1>
          <p className="text-slate-500 text-sm mt-1">ECU / SCU / Odo connector pinouts by brand &amp; part number.</p>
        </div>
        {isAdmin && (
          <Button data-testid="pinout-add-button" onClick={() => setDialog({ open: true, initial: { brand: brand === "All" ? "Honda" : brand, category: "ECU", pinout_type: "ECU" } })}
            className="bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
            <Plus className="h-4 w-4 mr-1.5" /> Add Pinout
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-4 mb-6 space-y-4">
        <div className="flex gap-2 flex-wrap">
          {tabs.map((b) => (
            <button key={b} data-testid={`pinout-brand-tab-${b}`} onClick={() => setBrand(b)}
              className={`px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wider border transition-colors ${
                brand === b ? TAB_ACTIVE[b] : "bg-[#121824] text-slate-400 border-slate-700 hover:border-slate-500"}`}>
              {b}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="pinout-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search model or part number…"
              className="pl-9 bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400 font-mono-tt text-sm" />
          </div>
          <select data-testid="pinout-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
            className="h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c} Pinout</option>)}
          </select>
          <select data-testid="pinout-type-filter" value={ptype} onChange={(e) => setPtype(e.target.value)}
            className="h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400">
            <option value="">All types</option>
            {PINOUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`pinout-card-${p.id}`} onClick={() => setDetail(p)}
            className="group relative rounded-xl border border-slate-800 bg-[#0c1017] overflow-hidden cursor-pointer hover:border-red-700/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
            <div className="relative aspect-[16/10] bg-[#121824]">
              <img src={p.image_url || FALLBACK_IMG} alt={p.model} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c1017] via-transparent to-transparent" />
              <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-mono-tt font-semibold uppercase border ${BRAND_STYLE[p.brand] || "text-slate-300 border-slate-600 bg-slate-900/50"}`}>{p.brand}</span>
              {p.category && p.category !== "ECU" && (
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-mono-tt font-semibold uppercase border text-slate-300 border-slate-600 bg-slate-900/70">{p.category}</span>
              )}
              {isAdmin && (
                <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`pinout-edit-btn-${p.id}`} onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="p-1.5 rounded bg-slate-900/90 text-slate-300 hover:text-cyan-300"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`pinout-delete-btn-${p.id}`} onClick={(e) => { e.stopPropagation(); del(p.id); }} className="p-1.5 rounded bg-slate-900/90 text-slate-300 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            <div className="p-2.5">
              <h3 className="font-head font-bold text-sm text-white leading-tight truncate">{p.model}</h3>
              {p.part_number && <p className="text-[11px] font-mono-tt text-cyan-300/90 mt-0.5 truncate">{p.part_number}</p>}
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Cpu className="h-3 w-3" /> {p.ecu_maker || "—"}</p>
            </div>
          </div>
        ))}
      </div>
      {filtered.length === 0 && <div className="text-center py-16 text-slate-600 font-mono-tt">No pinouts match your filters.</div>}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0c1017] border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {detail && (
            <div data-testid="pinout-detail">
              <div className="relative aspect-[16/9] bg-[#121824]">
                <img src={detail.image_url || FALLBACK_IMG} alt={detail.model} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  className="w-full h-full object-contain" />
                <button data-testid="pinout-detail-close" onClick={() => setDetail(null)} className="absolute top-3 right-3 p-1.5 rounded bg-slate-900/80 text-slate-300 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-mono-tt font-semibold uppercase border ${BRAND_STYLE[detail.brand] || "text-slate-300 border-slate-600 bg-slate-900/50"}`}>{detail.brand}</span>
                  {detail.ecu_maker && <span className="px-2 py-0.5 rounded text-[10px] font-mono-tt uppercase border border-slate-600 text-slate-300 bg-slate-900/50">{detail.ecu_maker}</span>}
                  {detail.category && <span className="px-2 py-0.5 rounded text-[10px] font-mono-tt uppercase border border-slate-600 text-slate-300 bg-slate-900/50">{detail.category}</span>}
                  {detail.pinout_type && <span className="px-2 py-0.5 rounded text-[10px] font-mono-tt uppercase border border-slate-600 text-slate-300 bg-slate-900/50">{detail.pinout_type}</span>}
                </div>
                <h2 className="font-head font-black text-2xl uppercase tracking-tight text-white">{detail.model}</h2>
                {detail.part_number && <p className="font-mono-tt text-cyan-300 text-sm mt-1">{detail.part_number}</p>}
                {detail.description && <p className="text-sm text-slate-400 mt-3">{detail.description}</p>}
                {detail.pins?.length > 0 && (
                  <div data-testid="pinout-assignment-table" className="mt-4">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 pb-1.5 mb-3">
                      <span className="w-16">Pin</span><span>Function</span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-3">
                      {detail.pins.map((pin, i) => {
                        const swatch = pin.color || wireColor(pin.pin);
                        return (
                          <div key={i} data-testid={`pinout-pin-row-${i}`} className="flex items-center gap-2">
                            <span data-testid={`pinout-pin-badge-${i}`}
                              className="inline-flex items-center justify-center min-w-[2.5rem] px-2.5 py-0.5 rounded font-mono-tt font-semibold text-xs border border-white/70"
                              style={swatch ? { backgroundColor: swatch, color: textOn(swatch) } : { color: "#f87171" }}>
                              {pin.function || pin.pin}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {detail.bench_notes && <p className="mt-4 text-xs font-mono-tt text-zinc-400/80 bg-zinc-950/30 border border-zinc-900/40 rounded p-3">⚡ {detail.bench_notes}</p>}
                {isAdmin && (
                  <div className="flex gap-2 mt-5">
                    <Button data-testid="pinout-detail-edit" onClick={() => { setDetail(null); openEdit(detail); }} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
                    <Button data-testid="pinout-detail-delete" onClick={() => del(detail.id)} variant="outline" className="border-red-900/60 text-red-400 hover:bg-red-950/40"><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CrudDialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o, initial: o ? dialog.initial : null })}
        title={dialog.initial?.id ? "Edit Pinout" : "Add Pinout"} fields={FIELDS} initial={dialog.initial}
        onSubmit={submit} testidPrefix="pinout" />
    </div>
  );
}
